import { useCallback, useMemo } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useListAttorneysQuery } from '@/services/api/attorneysService';

import { AddAttorneyDialog } from './AddAttorneyDialog';

type Props = {
  firmId: string;
  value: string | null | undefined;
  onChange: (attorneyId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  labelId?: string;
  /** Extra content rendered between the select and the "+ Add new attorney" link */
  children?: React.ReactNode;
};

function formatAttorneyLabel(
  firstName: string,
  lastName: string,
  email: string | null,
): string {
  const fullName = `${firstName} ${lastName}`.trim();

  if (!email) {
    return fullName;
  }

  return `${fullName} (${email})`;
}

export function AttorneySelect({
  firmId,
  value,
  onChange,
  placeholder = 'Select attorney',
  disabled,
  id,
  labelId,
  children,
}: Props): React.JSX.Element {
  const {
    data: attorneys = [],
    isLoading,
    refetch,
  } = useListAttorneysQuery({ firm_id: firmId }, { skip: !firmId });

  const options = useMemo(
    () =>
      attorneys.map((attorney) => ({
        id: attorney.id,
        label: formatAttorneyLabel(
          attorney.first_name,
          attorney.last_name,
          attorney.email,
        ),
      })),
    [attorneys],
  );

  const handleCreated = useCallback(async (createdId: string): Promise<void> => {
    await refetch();
    onChange(createdId);
  }, [onChange, refetch]);

  const handleChange = useCallback((nextValue: string): void => {
    onChange(nextValue === '__none__' ? null : nextValue);
  }, [onChange]);

  return (
    <div className="space-y-1.5">
      <Select
        value={value ?? undefined}
        onValueChange={handleChange}
        disabled={disabled || isLoading}
      >
        <SelectTrigger
          id={id}
          className="w-full"
          aria-label={placeholder}
          aria-labelledby={labelId}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">None</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.id} value={option.id}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {children}
      <AddAttorneyDialog
        firmId={firmId}
        onCreated={handleCreated}
        triggerLabel="+ Add new attorney"
        disabled={!firmId}
      />
    </div>
  );
}
